import { RichContentEntityType } from '@core/services/ai/ai-assistant.service';

const DEFAULT_ICONS: Record<RichContentEntityType, string> = {
  property: 'home',
  contract: 'description',
  tenant: 'person',
  loan: 'account_balance',
  expense: 'receipt',
  intervention: 'build',
  document: 'insert_drive_file',
  owner: 'person_outline',
  finance: 'paid',
  cockpit: 'dashboard',
};

/**
 * Resolve the in-app route for an AI-surfaced entity. Returns null when
 * the entity has no dedicated detail page (e.g. expenses are nested
 * under property/contract pages, owners/finance/documents have list-only
 * surfaces).
 *
 * Keep this aligned with `app.routes.ts`. The chat surfaces hundreds of
 * deep links — every divergence here breaks the click-through UX.
 */
export function buildEntityDeeplink(
  entityType: RichContentEntityType,
  entityId: string | null | undefined,
): string | null {
  if (!entityId) return null;
  switch (entityType) {
    case 'property':
      return `/properties/${entityId}`;
    case 'contract':
      // Lease detail lives under /contracts/detail/:id (→ redirects to
      // /overview). `/contracts/:id` matches no route and errors on click.
      return `/contracts/detail/${entityId}`;
    case 'tenant':
      return `/tenants/${entityId}`;
    case 'loan':
      // Same shape as contracts: detail is /loans/detail/:id, not /loans/:id.
      return `/loans/detail/${entityId}`;
    case 'intervention':
      // The repairs list is the primary intervention surface; maintenance
      // items deeplink to the same detail tree under /maintenance, but
      // the chat tools don't currently surface the maintenance/repair
      // distinction. Default to the repair detail.
      return `/interventions/repairs/detail/${entityId}`;
    case 'cockpit':
      return `/cards/${entityId}/workflow`;
    case 'expense':
    case 'document':
    case 'owner':
    case 'finance':
      // No dedicated detail page exposed by the app routes today.
      return null;
  }
}

export function defaultIconFor(entityType: RichContentEntityType): string {
  return DEFAULT_ICONS[entityType] ?? 'circle';
}
